/**
 * 20260919 - IPHONE DUO SIDE DOCK: what the web layer is allowed to know about the fold.
 *
 * THE RULE WE ARE FOLLOWING (Apple, HIG + design talk 111466, and docs/reference/iphone-duo.md §5.1):
 * never detect the device or the pose. Adapt to size class, to the per-side safe areas, and to the
 * regions the system has reserved. Nothing in this file names the Duo, and nothing in it branches on
 * a device string — a second foldable, or an iPhone that grows a reserved strip for some other
 * reason, gets the same answer for the same insets.
 *
 * WHY THE WEB LAYER CANNOT WORK THIS OUT ALONE. Safari/WKWebView on iOS 27.1 ships NO fold signal:
 * `window.viewport.segments` is absent, `navigator.devicePosture` is absent, `env(viewport-segment-*)`
 * is invalid, and `@media (horizontal-viewport-segments: N)` / `(device-posture: folded)` parse but
 * always evaluate false (the implementation is in the binary behind disabled preference keys — see
 * iphone-duo.md §4.3 for the measurement). So posture and the fold rectangle can only arrive from
 * Unity, as the `setFoldState` bridge verb. What the web layer CAN see on its own is the per-side
 * safe area, and on the Duo's outer display that is enough for the one thing this batch needs: the
 * system reserves a vertical strip on the trailing edge for its status bar, camera housing and
 * vertical bar buttons, and that strip arrives as an ASYMMETRIC `env(safe-area-inset-right)`.
 *
 * ASYMMETRY IS THE WHOLE SIGNAL — WHEN THERE IS A RESERVED STRIP TO BE ASYMMETRIC ABOUT. Every
 * non-foldable iPhone in landscape reports a SYMMETRIC pair (~59px both sides — the notch/island
 * clears the same distance either way), an iPad reports 0/0, and a Mac window reports 0/0. Only a
 * reserved side strip pushes ONE side out on its own. That is what deriveVerticalBarEdge() reads,
 * and it is why the thresholds below are a wide band rather than an equality test: the exact inset
 * is Apple's to change, the asymmetry is not.
 *
 * 20260919 - AND IT IS NOT THE ONLY SIGNAL, WHICH WE LEARNED BY MEASURING. In SPLIT VIEW on the
 * inner display the insets are 0/0/0/0 — in BOTH pane positions, measured twice by the owner with
 * the arrangement mirrored, so the zeros are TRUE and not a plumbing failure — yet the system still
 * has a preferred edge for a vertical bar there, and Safari uses it: Safari's pane is 469 pt wide
 * and its layout viewport 385 pt, the missing 84 pt being its own vertical rail with the system
 * clock at the top of it. Nothing in four zeros can tell us that. The authority in this pose is
 * UIKit's `verticalBarEdge` trait, which "reflects the system's preferred edge REGARDLESS of
 * whether a vertical bar is currently visible" (UIVerticalBarEdge.h, 27.1 SDK) and which only the
 * native layer can read — so it arrives the same way posture does, over the bridge (and, for the
 * first frame, in the page URL: platform/urlSafeArea.ts). When an edge is known, the inset on it is
 * zero AND the host says this window is one PANE of an arrangement, the system has reserved nothing
 * and the space is ours to carve: see SYNTHESIZED_VERTICAL_BAR_WIDTH and resolveVerticalBar()
 * below, which are where that one decision lives for every consumer — both halves of it, the width
 * and whether the system reserved it.
 *
 * 20260919 - AND THE PANE IS PART OF THE TEST, NOT DECORATION. The trait names an edge "regardless
 * of whether a vertical bar is currently visible" and goes Unspecified only where the CONTEXT never
 * places one — which is not the same as "this app has no bar". A full-screen session on the inner
 * display is therefore allowed to name an edge and report four zeros, and nobody has measured what
 * it actually does; synthesizing on the edge alone would carve 84px out of that viewport on the
 * strength of an assumption. The pane is the pose we MEASURED, so the pane is what we act on. See
 * FoldState.splitView.
 */

export type FoldPosture = 'closed' | 'partiallyOpen' | 'fullyOpen' | 'unknown';

/** Which edge the system's vertical bar (and the strip it reserves) sits on, in WRITING-DIRECTION
 *  terms, not left/right — the same vocabulary as UIKit's `verticalBarEdge` trait, so an RTL build
 *  and the native side agree without a translation step. */
export type VerticalBarEdge = 'leading' | 'trailing';

/** CSS px. Named per side rather than as a DOMRect because the per-side value IS the adaptation
 *  Apple asks for; a bounding box would throw the asymmetry away. */
export interface SafeAreaInsets { left: number; top: number; right: number; bottom: number }

/** 20260929 - The laptop pose's fold band as fractions of the window's height, top-left origin. See
 *  FoldState.laptopBand. */
export interface LaptopBand { top: number; height: number }

/** The fold itself, when the host tells us about it. `rect` is CSS px in viewport coordinates, so a
 *  component can avoid the hinge without converting anything. */
export interface FoldDivision {
  active: boolean;
  axis: 'vertical' | 'horizontal';
  rect: { x: number; y: number; width: number; height: number };
}

export interface FoldState {
  /** Does this window sit on a display with a division region at all? FALSE for every non-foldable
   *  host, which is every host today until Unity starts pushing the verb. */
  foldable: boolean;
  display: 'inner' | 'outer' | 'unknown';
  posture: FoldPosture;
  division: FoldDivision | null;
  verticalBarEdge: VerticalBarEdge | null;
  /**
   * 20260919 - IS THIS WINDOW ONE PANE OF AN ARRANGEMENT? Reported by the host, never inferred —
   * and since it is the GATE on the synthesized rail below (resolveVerticalBarWidth), it is state
   * and not a curiosity.
   *
   * WHY THE EDGE ALONE IS NOT ENOUGH, which is what this field is here to fix. `verticalBarEdge`
   * "reflects the system's preferred edge REGARDLESS of whether a vertical bar is currently
   * visible" (UIVerticalBarEdge.h, 27.1 SDK); it is `Unspecified` only where the CONTEXT never
   * places one, not where THIS app happens to have no bar. So "an edge is named and the inset on
   * it is zero" does not, on its own, mean the space is ours: a full-screen session on the inner
   * display could name an edge and report zeros too, and carving 84px out of that viewport would
   * be a rail nobody asked for. The pose we actually MEASURED is the pane — 469 × 669, insets
   * 0/0/0/0 in both positions, Safari spending 84 of its own 469 on a rail — and this flag is what
   * says we are in it.
   *
   * NOT A MODE TEST, for all that it reads like one. It does not select a layout and nothing may
   * branch on it to choose a design; it answers one factual question — "has the system given this
   * window less than a display to itself?" — which is precisely the question that distinguishes
   * the measured pose from the one nobody has measured yet.
   *
   * `false` means "not reported as a pane", which is also what every host that cannot say means.
   * The safe direction: a false negative costs the dock in a pose that could have had one, a false
   * positive puts a column of controls over a full-screen page.
   */
  splitView: boolean;
  /**
   * 20260920 - IS THE SYSTEM'S OWN FURNITURE AT THE HEAD OF THE STRIP / AT THE FOOT? Three values
   * each, and the third one is the point: `null` means NOBODY COULD SAY, which is not `false`.
   *
   * WHAT THEY REPLACE. The dock used to reserve 96px for the camera housing at BOTH ends of its
   * column whenever the system owned the strip, because nothing reaching the web layer could name
   * the occupied end — the strip is outside the safe rect, so a circle inside it moves none of the
   * four insets. That was right in half of each pose and dead space in the other half: the owner,
   * 20260920, on a closed portrait pose, "the bottom buttons are still far too high up". iOS 27.1's
   * reserved-region API names the end, Unity reads it (UnityAppControllerOverride.mm carries the
   * measurement and the classification rule) and pushes it as `barHeadOccluded` / `barFootOccluded`.
   *
   * `true` = furniture at that end, spend the allowance. `false` = MEASURED CLEAR, spend nothing.
   * `null` = no reading at all (every host before 27.1, every non-iOS target, the harness, and any
   * iOS binary built against the 27.0 SDK), and resolveHousingEnds() below is where that third case
   * turns back into a geometry — deliberately NOT here, because the fallback depends on whose strip
   * it is and that is the resolver's question, not the field's.
   *
   * 20260920 - AND THE URL SEED ANSWERS IT TOO, which it did not on the morning of the same day.
   * The pair rides in the page URL beside the four insets (platform/urlSafeArea.ts) and, alone
   * among the seed's fields, OUTLIVES the seed's expiry — because `env()` has a live opinion about
   * every other field and none at all about this one. foldStateStore.ts carries that argument and
   * the pose change that bounds it.
   *
   * REPORTED, NEVER INFERRED, like `splitView` above: there is no web-visible signal for either.
   */
  barHeadOccluded: boolean | null;
  barFootOccluded: boolean | null;
  /**
   * 20260926 - HOW FAR THE SYSTEM'S FURNITURE REACHES INTO THE STRIP FROM EACH END, in CSS px (a
   * point is a CSS px in this WebView). The occlusion pair says WHICH end is occupied; these say
   * how much of it. Measured on the closed pose: the housing/status zone is 170 at the head in
   * portrait and 82 at the housing end in either landscape rotation — so a single 96px allowance
   * put the head's first control inside the zone in portrait and wasted space in landscape.
   *
   * Unity reads them from the same 27.1 reserved-region classification as the pair and sends them
   * beside it (URL `safeAreaBarHeadExtent` / `safeAreaBarFootExtent`, push `barHeadExtent` /
   * `barFootExtent`). They ride with the pair and live exactly as long: the store holds them past
   * the seed's expiry and drops them on the same pose change (foldStateStore.ts).
   *
   * `null` = not reported (every older host, the harness rows that declare no reading, a malformed
   * value). `0` is what native sends for an end it measured CLEAR, and also its spelling of "the
   * end is occupied but the extent could not be read" — so resolveHousingEnds() treats anything
   * that is not a positive number as unknown, which keeps the 96px fallback (never a 24px sliver).
   * Meaningless at an end the pair does not report as occluded.
   */
  barHeadExtent: number | null;
  barFootExtent: number | null;
  /**
   * 20260929 - THE LAPTOP POSE'S FOLD BAND, as WINDOW FRACTIONS with a top-left origin (`top` and
   * `height`, each 0..1 of the window's height). Non-null only while the host reports the laptop pose:
   * an active horizontal division with the hinge neither closed nor flat and the window not a Split
   * View pane — Unity's fold monitor answers all of that (FoldPoseMonitor.LaptopPoseActive) and sends
   * the band beside `posture: 'partiallyOpen'`.
   *
   * FRACTIONS, NOT CSS PX, on purpose, and NOT `division`: the push can land before the page's own
   * `resize` for the pose change (TimelineWebViewPanel.cs, the early-edge note), so a px rect would be
   * a measurement of the old viewport. A fraction converts on every resize (resolveLaptopSplit below),
   * which is the convention the canvas overlay's `setOverlayRegion` already uses. `division` keeps its
   * px contract and stays null from the native push.
   *
   * `null` = not the laptop pose, or nobody could say — every host but the board route's native push
   * and the preview harness.
   */
  laptopBand: LaptopBand | null;
  safeArea: SafeAreaInsets;
  /** Which of the four inputs answered. Load-bearing for the CSS-variable publisher in
   *  foldStateStore.ts: every source EXCEPT `env` overrides what `env()` already says, because
   *  `env` IS what the CSS already resolves to.
   *
   *  20260919 - `'url'` is the native measurement Unity put in the page URL (platform/urlSafeArea.ts).
   *  It is a BOOT SNAPSHOT, not a live source — it cannot see a later fold — so foldStateStore
   *  retires it as soon as `env` has a real reading or the boot settle ladder ends. */
  source: 'native-bridge' | 'harness' | 'url' | 'env';
}

/**
 * What we assume in the absence of any host: not foldable, pose unknown, no fold, no reserved strip.
 *
 * "Unknown" rather than "fullyOpen" on purpose. Standalone and online review are fold-BLIND by
 * design (iphone-duo.md §4.3) and the community consensus is to treat 440–960pt as a continuum
 * rather than guess a pose; a confident wrong posture would let a component branch on something it
 * cannot actually see, which is the exact mistake Apple's rule forbids.
 */
export const ASSUMED_FOLD_STATE: FoldState = {
  foldable: false,
  display: 'unknown',
  posture: 'unknown',
  division: null,
  verticalBarEdge: null,
  splitView: false,
  // Null, not false: a host that never reported an occlusion has not told us the ends are clear —
  // see the field docs, and resolveHousingEnds() for what the absence resolves to.
  barHeadOccluded: null,
  barFootOccluded: null,
  // 20260926 - and no extents, for the same reason: nobody measured them.
  barHeadExtent: null,
  barFootExtent: null,
  // 20260929 - and no laptop band: a browser cannot see the hinge.
  laptopBand: null,
  safeArea: { left: 0, top: 0, right: 0, bottom: 0 },
  source: 'env',
};

/**
 * 20260929 - one laptop band off the wire: an object with a finite `top` in 0..1 and a finite
 * `height` in (0..1], the band inside the window; anything else is `null` ("not the laptop pose").
 * The same shape check as the canvas overlay's readOverlayRegion (bridge/features/canvasOverlay.ts),
 * shared by the setFoldState guard and the store so the two cannot disagree about what a band is.
 */
export function readLaptopBand(raw: unknown): LaptopBand | null {
  const b = raw as Partial<LaptopBand> | null | undefined;
  if (b === null || b === undefined || typeof b !== 'object') return null;
  if ([b.top, b.height].some((n) => typeof n !== 'number' || !Number.isFinite(n))) return null;
  const { top, height } = b as LaptopBand;
  const EPS = 1e-6;
  if (top < 0 || height <= 0 || top + height > 1 + EPS) return null;
  return { top, height };
}

/** 20260929 - The regions of a laptop split, in CSS px from the window's top. */
export interface LaptopSplitRegion { y: number; h: number }

/**
 * 20260929 - THE LAPTOP SPLIT, in CSS px: the TOP region (from the safe top down to the band — the
 * passive picture), the BAND itself (the crease), and the BOTTOM region (from the band's bottom edge
 * to the window's bottom — the touch surface). resolveLaptopSplit() is the only producer.
 */
export interface LaptopSplit {
  top: LaptopSplitRegion;
  band: LaptopSplitRegion;
  bottom: LaptopSplitRegion;
}

/** 20260929 - the bottom region's floor, in CSS px: the Timeline tab's own chrome (shell bar, header,
 *  info bar, lanes, transport) is about 300 at the tablet sizes, so a smaller bottom region could not
 *  hold it at all. */
export const LAPTOP_SPLIT_MIN_BOTTOM = 300;

/** 20260929 - the top region's floor, in CSS px: parity with the canvas split's 150-unit floor — a
 *  picture shorter than that is not worth a region of its own. */
export const LAPTOP_SPLIT_MIN_TOP = 150;

/**
 * 20260929 - THE ONE DECISION "split this window at the fold, or not", for the Timeline tab's
 * playback mirror (components/TimelinePlaybackMirror.tsx, mounted by SceneBoardPage).
 *
 * Null — no split, the ordinary full-height layout — unless every one of these holds:
 *   • the host reported a laptop band (only the native push and the harness can);
 *   • the posture is `partiallyOpen` (what the push sends with the band) or `unknown`;
 *   • the window is not a Split View pane (Unity already vetoes panes; this is the web's own belt);
 *   • no side dock is in effect (resolveVerticalBar width 0) — the two layouts never stack;
 *   • a real viewport, with the band inside it;
 *   • the top region is at least LAPTOP_SPLIT_MIN_TOP and the bottom one LAPTOP_SPLIT_MIN_BOTTOM.
 *
 * The fractions convert against the viewport handed in, so a caller re-asks on every resize and the
 * answer follows a push that landed before the page's own resize. Whole px, so the harness's 455 / 40
 * band on a 951-tall window resolves to exactly 455 / 40.
 */
export function resolveLaptopSplit(
  state: Pick<FoldState, 'laptopBand' | 'posture' | 'splitView' | 'verticalBarEdge' | 'safeArea'>,
  viewportWidth: number,
  viewportHeight: number,
): LaptopSplit | null {
  const band = readLaptopBand(state.laptopBand);
  if (band === null) return null;
  if (state.posture !== 'partiallyOpen' && state.posture !== 'unknown') return null;
  if (state.splitView === true) return null;
  if (resolveVerticalBar(state).width !== 0) return null;
  if (!Number.isFinite(viewportWidth) || viewportWidth <= 0) return null;
  if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) return null;

  const bandY = Math.round(band.top * viewportHeight);
  const bandH = Math.round(band.height * viewportHeight);
  const bandBottom = bandY + bandH;
  if (bandH <= 0 || bandBottom > viewportHeight) return null;

  const safeTop = Number.isFinite(state.safeArea.top) && state.safeArea.top > 0 ? state.safeArea.top : 0;
  const topH = bandY - safeTop;
  const bottomH = viewportHeight - bandBottom;
  if (topH < LAPTOP_SPLIT_MIN_TOP || bottomH < LAPTOP_SPLIT_MIN_BOTTOM) return null;

  return {
    top: { y: safeTop, h: topH },
    band: { y: bandY, h: bandH },
    bottom: { y: bandBottom, h: bottomH },
  };
}

/**
 * 20260926 - THE LARGEST OCCLUSION EXTENT WE WILL BELIEVE, in CSS px. The same "this cannot be a
 * system region" line as MAX_URL_SAFE_AREA_INSET (platform/urlSafeArea.ts) and for the same
 * reason: the tallest zone measured is 170 at the head of a 678 strip, and anything past 400 would
 * eat a phone-height column whole. A value outside 0..400 is not a reading; the extents are
 * dropped and the pair is kept, which lands the dock on the 96px allowance at that end.
 */
export const MAX_OCCLUSION_EXTENT = 400;

/**
 * 20260926 - THE AIR BETWEEN AN OCCLUSION ZONE AND THE FIRST CONTROL OUTSIDE IT, in CSS px.
 * Measured off Safari on the closed pose: its first rail button's top sits ≈ 24 below the 170pt
 * status zone. An occluded end with a known extent therefore clears `extent + 24`; an occluded end
 * whose extent is unknown keeps the stylesheet's 96px allowance. The Unity dock uses the same
 * number (SideDockButtonHost.OcclusionGapPoints), so both shells agree.
 */
export const OCCLUSION_CLEARANCE_GAP = 24;

/** 20260926 - one extent off the wire: a finite number in 0..MAX_OCCLUSION_EXTENT, else `null`.
 *  Shared by the bridge guard and the store so the two cannot disagree about what a reading is. */
export function readOcclusionExtent(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value)
    && value >= 0 && value <= MAX_OCCLUSION_EXTENT
    ? value
    : null;
}

/**
 * UIKit's minimum hit target, and therefore the narrowest inset worth calling a reserved bar:
 * below it nothing of ours could sit in the strip anyway. Exported (not a bare literal) because
 * TWO decisions turn on the same number and they must not drift — deriveVerticalBarEdge() uses it
 * to infer an edge from measured insets, and hooks/useSideDock.ts uses it to refuse a strip that a
 * PUSHED verticalBarEdge claims but that is too narrow to dock anything into.
 */
export const MIN_VERTICAL_BAR_INSET = 44;

/**
 * The one inference the web layer makes on its own: is ONE side's inset large enough, and the other
 * small enough, that the system must have reserved a vertical strip there?
 *
 * 44 is UIKit's minimum hit target — below it nothing could be docked in the strip anyway, so it is
 * the smallest inset worth calling a bar. 22 (half of it) is the ceiling for "the other side is just
 * a rounded corner or a notch clearance". The gap between them is deliberate: a pair that falls in
 * it is ambiguous, and an ambiguous answer must be `null`, never a guess.
 *
 * The cases that MUST answer null, because getting any of them wrong moves controls on hardware
 * that has no reserved strip at all:
 *   • a classic iPhone in landscape — SYMMETRIC ~59/59, so neither clause fires;
 *   • an iPad, a Mac window, and every browser — 0/0;
 *   • the Duo's INNER display — no side strip, so 0/0 again.
 * Only an asymmetric pair (the Duo outer display's ≈84/0) resolves to an edge.
 */
export function deriveVerticalBarEdge(safeArea: SafeAreaInsets): VerticalBarEdge | null {
  const { left, right } = safeArea;
  if (!Number.isFinite(left) || !Number.isFinite(right)) return null;
  const other = MIN_VERTICAL_BAR_INSET / 2;
  if (right >= MIN_VERTICAL_BAR_INSET && left < other) return 'trailing';
  if (left >= MIN_VERTICAL_BAR_INSET && right < other) return 'leading';
  return null;
}

/**
 * 20260920 - WHICH SIDE THE DOCK GOES ON, when a host has told us a trait AND the browser has
 * measured the insets: THE MEASUREMENT WINS WHENEVER IT NAMES A SIDE.
 *
 * THE DEFECT. This was the other way round everywhere — the pushed `verticalBarEdge` was taken
 * whenever it was present and `deriveVerticalBarEdge()` was only its fallback. The owner turned a
 * Duo 180° in the closed landscape pose on 20260920 and photographed the result: the page inset
 * itself correctly on the LEFT (the CSS half resolves `--safe-*` straight from `env()`), and the
 * dock stayed on the RIGHT, drawn over the panel, because the last trait anyone pushed still said
 * `trailing`. A dock on the wrong side is not a degraded layout, it is a column of controls on top
 * of the content.
 *
 * THEY ARE NOT THE SAME QUESTION, WHICH IS WHY NEITHER ONE CAN SIMPLY OUTRANK THE OTHER:
 *   • THE TRAIT answers "which edge would the system PLACE a vertical bar on" — and it answers it
 *     "regardless of whether a vertical bar is currently visible" (UIVerticalBarEdge.h, 27.1 SDK).
 *     It is a preference, and it can be named in a window where nothing has been reserved at all.
 *   • THE INSETS answer "where has the system actually RESERVED space" — the only question the dock
 *     can be laid out from, because the column is pinned flush to a window edge (inset-inline-* +
 *     an offset of 0). Put it on the side with no reservation and it lands on the page.
 *
 * So an UNAMBIGUOUS asymmetric pair — which is the only thing deriveVerticalBarEdge will call a
 * side at all — settles it, and the trait decides only where the insets cannot: four zeros, or a
 * symmetric pair. Four zeros is the Split View pane the trait exists for (measured, twice,
 * mirrored), where deriveVerticalBarEdge returns null BY DESIGN and the trait is the only thing
 * that knows a rail belongs on that side. Nothing about that pose regresses here: a reading that
 * says nothing cannot outrank one that says something.
 *
 * AND THE TRAIT IS STILL LOAD-BEARING, so do not "simplify" this to the derivation alone. It is
 * what carries the pane, and it is the only source that can name an edge whose inset is under
 * MIN_VERTICAL_BAR_INSET — a case resolveVerticalBar() then refuses on its own terms.
 *
 * `trait` is taken as unknown-ish (`| null | undefined`, validated here) so wire data and a parsed
 * URL seed can both be handed straight in without each call site re-spelling the same two literals.
 */
export function resolveVerticalBarEdge(
  trait: VerticalBarEdge | null | undefined,
  safeArea: SafeAreaInsets,
): VerticalBarEdge | null {
  const measured = deriveVerticalBarEdge(safeArea);
  if (measured !== null) return measured;
  return trait === 'leading' || trait === 'trailing' ? trait : null;
}

/**
 * 20260919 - THE RAIL WE CARVE FOR OURSELVES WHEN THE SYSTEM HAS RESERVED NOTHING.
 *
 * 84 CSS px. Not a guess and not a device dimension: it is what APPLE spends on a vertical rail in
 * the very pose this constant is for. Measured on the owner's Duo simulator (inner display, Split
 * View, iOS 27.1): each pane is 469 pt wide, and Safari's pane reports a layout viewport of 385 pt
 * — 469 − 385 = 84 pt of pane that Safari gives to its own vertical rail, with the system's clock
 * and wifi glyph painted at the top of it and Safari's own controls (≈ 44–48 pt across) stacked
 * down it. A rail of ours that is narrower than the system's in the same pose would look like a
 * mistake next to it; a wider one would eat the pane. It also happens to equal the strip the system
 * reserves on the outer display in the closed pose (`env(safe-area-inset-right)` ≈ 84), so the dock
 * is the same width in every pose that has one — which is the point: a control must not change size
 * because the window did.
 *
 * WHY A CONSTANT IS CORRECT HERE AND A MEASUREMENT IS CORRECT EVERYWHERE ELSE. When the system HAS
 * reserved a strip, the strip is Apple's and its width is theirs — we read it, we never assume it
 * (that is the rule SideDock.module.css states, and it still holds). In Split View the system has
 * reserved nothing at all from us: the pane's safe-area insets are 0/0/0/0 in BOTH pane positions
 * (owner measured it twice, mirrored — the zeros are true, not a plumbing failure), while the
 * window's `verticalBarEdge` trait still names an edge, because that trait "reflects the system's
 * preferred edge regardless of whether a vertical bar is currently visible" (UIVerticalBarEdge.h,
 * 27.1 SDK). So the edge is known and the width is not reserved by anybody: the space is ours to
 * take, and taking exactly what Apple takes is the honest number.
 *
 * AND IT IS SPENT ONLY IN THE POSE IT WAS MEASURED IN — see the split-view gate in
 * resolveVerticalBarWidth below. This constant is 84 because of what Safari does IN A PANE; it is
 * not a general answer to "an edge with no inset", because the only other place that shape could
 * arise is a full-screen inner-display session nobody has probed.
 */
export const SYNTHESIZED_VERTICAL_BAR_WIDTH = 84;

/**
 * 20260920 - WHAT THE STRIP IS, IN ONE ANSWER: how wide it is, and WHO RESERVED IT.
 *
 * `reserved` is not decoration on the width — it is the other half of the same decision, and it
 * has one consumer with real geometry riding on it. SideDock.module.css reserves room for the
 * CAMERA HOUSING at both ends of the column, because in the CLOSED pose the strip is the system's
 * own furniture and the housing is physically inside it, at an end the insets cannot name. A
 * SYNTHESIZED rail has no housing in it at all: it is ordinary pane width that we carved for
 * ourselves on a display that has no camera in that edge. Paying the housing reserve there costs
 * 96px at each end of a 669px column for nothing — which is exactly the gap the owner reported on
 * 20260920 ("the bottom buttons aren't being anchored to the bottom… a large gap you don't see on
 * the Safari side").
 *
 * So the distinction is SURFACED rather than re-derived downstream. A consumer that asked
 * "splitView?" instead would be asking a pose question to get a furniture answer, and would go
 * wrong the first time the system reserves a real strip in some pane, or names a rail in a pose we
 * have not measured. The question the reserve actually asks is "did the system reserve this
 * strip?", and this is where it is answered.
 */
export interface VerticalBarResolution {
  /** CSS px available on the docked edge, and 0 for "no dock at all" — the same number
   *  resolveVerticalBarWidth() has always returned. */
  width: number;
  /**
   * TRUE: the SYSTEM reserved this strip and told us how wide. We are sitting inside Apple's
   * furniture — its status bar, its vertical-bar buttons and, in the closed pose, the camera
   * housing are all in there with us, so the clearances at the ends of the column are owed.
   *
   * FALSE: nobody reserved anything and the rail is ours — carved out of ordinary pane width on a
   * display whose camera is not in this edge (Split View). Nothing of the system's is in the
   * column, so nothing at the ends of it has to be cleared.
   *
   * Always FALSE when `width` is 0: there is no strip, so it has no owner.
   */
  reserved: boolean;
}

/** Shared, so "no dock" is reference-stable for the memoising callers. */
const NO_VERTICAL_BAR: VerticalBarResolution = { width: 0, reserved: false };

/**
 * THE ONE PLACE the dock's strip is decided, for every consumer — hooks/useSideDock.ts (the column
 * itself) and foldStateStore.ts's `--safe-*` publisher (the page content that has to inset out of
 * it) both come through here, so the dock and everything beside it can never disagree about how
 * wide the rail is. Takes only the three fields it reads, so a preset row or a half-built pose can
 * be asked the same question as a live FoldState.
 *
 * Four answers, and each is a different situation:
 *
 *   • NO EDGE → 0, unreserved. Nothing is docked; the shell lays out exactly as it always has.
 *     Every non-foldable host lands here.
 *
 *   • AN EDGE WITH A USABLE INSET → the inset, RESERVED. The system reserved a strip and told us
 *     how wide; we sit inside it at ITS width, never a constant of ours (the Duo outer display,
 *     closed).
 *
 *   • AN EDGE WITH A ZERO INSET, IN A PANE → SYNTHESIZED_VERTICAL_BAR_WIDTH, NOT reserved. The
 *     system named an edge but reserved no space — Split View, where the insets are honestly
 *     0/0/0/0 and the trait is the only thing that knows a rail belongs on that side. There is no
 *     strip to sit inside, so we carve one: the page content insets by this number (the `--safe-*`
 *     publisher) and the dock fills it, which is precisely the geometry the closed pose already
 *     produces. Reusing that machine is why almost nothing downstream — the aliases, the cs:6133
 *     scrim padding, the board and projects shells — has to know which of the two it is looking
 *     at. The ONE thing that does is the dock's own housing reserve, and `reserved` is how it
 *     knows (20260920; see VerticalBarResolution above).
 *
 *   • AN EDGE WITH A ZERO INSET AND NO PANE → 0. 20260919, and the one case this function used to
 *     get wrong. See the gate below.
 *
 * THE GATE, AND WHY IT IS NOT BELT-AND-BRACES. `verticalBarEdge` "reflects the system's preferred
 * edge REGARDLESS of whether a vertical bar is currently visible" and is `Unspecified` only where
 * the CONTEXT never places one — NOT where this app has no bar. So "edge named, inset zero" is a
 * shape that a FULL-SCREEN session on the inner display is allowed to produce as well, and nobody
 * has measured whether it does. Synthesizing on the edge alone would spend 84px of somebody's
 * full-screen viewport on the strength of that unmeasured assumption, and the failure would be
 * silent: a fixed column of controls beside content that has been shoved 84px inboard for it.
 * The pane is what we measured (469 × 669, zeros in both positions, Safari's own 84pt rail), so
 * the pane is what we act on, and the two cases stop being the same shape.
 *
 * WHEN THE GATE COMES OFF: when somebody measures what the trait actually returns on the
 * FULL-SCREEN inner display (iphone-duo.md §2.2 is the checklist that pose belongs to). If it is
 * Unspecified there, the flag is redundant and this clause can go back to reading the edge alone.
 * If it names an edge, this gate is the only thing standing between us and a rail in a pose that
 * never wanted one — and the right follow-up is a second measurement, not a second guess.
 *
 * AND THE REFUSAL STAYS, for the case in between: an edge whose inset is NON-ZERO but under
 * MIN_VERTICAL_BAR_INSET is still reported as no dock at all, pane or no pane. That is not the same
 * case as a zero. A non-zero inset means the system HAS reserved that strip and has told us its
 * width, so widening it is not ours to do — and we cannot use it as-is either, because the dock is
 * pinned flush to the window edge: a 32px control in a 12px strip hangs off the window, where the
 * viewport clips it rather than letting it overhang inboard. Falling back to the header is the
 * answer that always fits. Only "the system reserved NOTHING here, and it is because we are in a
 * pane" is an invitation to take the space.
 *
 * THE PARAMETER TAKES THE FLAG EXPLICITLY — `Pick<…, 'splitView'>` and not an optional — so that
 * every call site has to have an answer to it. A caller that could omit it would get the pre-gate
 * behaviour back by accident, which is exactly the class of silent drift this batch is cleaning up.
 */
export function resolveVerticalBar(
  state: Pick<FoldState, 'verticalBarEdge' | 'safeArea' | 'splitView'>,
): VerticalBarResolution {
  const edge = state.verticalBarEdge;
  if (edge === null) return NO_VERTICAL_BAR;
  const inset = edge === 'trailing' ? state.safeArea.right : state.safeArea.left;
  if (!Number.isFinite(inset) || inset < 0) return NO_VERTICAL_BAR;
  if (inset === 0) {
    return state.splitView === true
      ? { width: SYNTHESIZED_VERTICAL_BAR_WIDTH, reserved: false }
      : NO_VERTICAL_BAR;
  }
  return inset < MIN_VERTICAL_BAR_INSET
    ? NO_VERTICAL_BAR
    : { width: inset, reserved: true };
}

/**
 * 20260920 - WHICH END OF THE COLUMN OWES A CLEARANCE, which is the question the dock's stylesheet
 * has always actually been asking.
 *
 * THE TWO DEFECTS THIS CLOSES, both reported by the owner on 20260920 against cs:6135:
 *   • CLOSED POSE, "the bottom buttons are still far too high up". The camera is at the HEAD of the
 *     strip (measured: two active occlusion regions, both centred in the top half of a 678pt
 *     window). The 96px the foot was paying cleared nothing.
 *   • SPLIT VIEW PANE, "the toggle now appears SLIGHTLY too high… it should move down to start from
 *     the same height as the time". cs:6135 had just taken the reserve off BOTH ends of a carved
 *     rail on the grounds that a pane has no camera in it. True — and beside the point, because iOS
 *     paints its status circle at the HEAD of the rail in the pane that holds it, which is exactly
 *     the furniture the head clearance exists to sit below.
 *
 * So the predicate moved: it is no longer "whose strip is this" (resolveVerticalBar's `reserved`,
 * which is still the right question for the WIDTH) but "which end is occluded". `reserved` becomes
 * an INPUT to the fallback rather than the answer.
 *
 * FOUR RULES, in order:
 *
 *   1. A REPORTED BOOLEAN ALWAYS WINS, at each end independently. `false` is a measurement — the
 *      native side asked the 27.1 reserved-region API and there was nothing at that end — so it
 *      spends nothing. Nothing here second-guesses it: a host that can see the regions knows more
 *      about this window than any rule in this file.
 *
 *   2. NOT REPORTED, AND THE STRIP IS THE SYSTEM'S → both ends. Exactly the geometry that shipped
 *      before the occlusion keys existed, kept deliberately: the housing is bolted to one physical
 *      corner, so 180° of rotation moves it to the other end of the strip in every orientation, and
 *      with no reading there is no way to say which. Wasted space, never a control under a camera.
 *
 *   3. NOT REPORTED, AND THE RAIL IS ONE WE CARVED → the HEAD only. The owner described Safari in
 *      the same pane: in Split View it "mirrors the closed positioning, allowing for the OS to put
 *      the status bar info in a circle where the camera was". There is no camera in a pane, but
 *      there IS system chrome at the head of the rail, and the owner's own screenshot shows the
 *      clock sitting there. The foot of a pane is measured clear — 0/0/0/0 in both positions — so
 *      it owes nothing. This is the clause that fixes complaint 2 even on a host that never reports
 *      a region for the status circle; where iOS DOES report one, rule 1 replaces it.
 *
 *   4. NO DOCK AT ALL → neither end. There is no column, so there is nothing to clear.
 *
 * Note that rules 2 and 3 agree about the HEAD and differ only about the FOOT. That is not an
 * accident of the code shape: in every pose anyone has measured there is system furniture at the
 * head of the strip, and the only open question has been whether there is any at the foot.
 */
export interface HousingEnds {
  /** Furniture at the head of the strip — the top of the window, since the dock is a column pinned
   *  `top: 0; bottom: 0` in every pose it exists in. */
  head: boolean;
  /** Furniture at the foot. */
  foot: boolean;
  /**
   * 20260926 - HOW FAR the furniture at the head reaches down the strip, in CSS px — present only
   * where the head was REPORTED occluded with a positive extent. Absent (never 0) everywhere else:
   * a clear head, an unreported head resting on the rule-2/rule-3 fallback, or an occluded head
   * whose extent native could not read. Absent is what keeps the 96px allowance.
   */
  headExtent?: number;
  /** 20260926 - the same, for the foot. */
  footExtent?: number;
}

/** Shared, so "no dock" is reference-stable for the memoising callers — same reason as
 *  NO_VERTICAL_BAR above. */
const NO_HOUSING: HousingEnds = { head: false, foot: false };

/** 20260926 - an extent is spent only at an end REPORTED occluded, and only when it is a positive
 *  reading: native writes 0 for an end it could not measure, and a 0 + 24 clearance would put a
 *  control inside the zone the 96px fallback exists to clear. */
function knownExtent(occluded: boolean | null, extent: number | null | undefined): number | undefined {
  if (occluded !== true) return undefined;
  const value = readOcclusionExtent(extent);
  return value !== null && value > 0 ? value : undefined;
}

export function resolveHousingEnds(
  state: Pick<FoldState,
    'verticalBarEdge' | 'safeArea' | 'splitView' | 'barHeadOccluded' | 'barFootOccluded'>
    // 20260926 - optional here (not on FoldState) so a caller that only models the pair — the
    // column budget, the per-end tests — can keep asking the question it always asked.
    & Partial<Pick<FoldState, 'barHeadExtent' | 'barFootExtent'>>,
): HousingEnds {
  // Asked through resolveVerticalBar rather than off the edge, because "is there a dock" and "whose
  // strip is it" are both answers it already gives, and a second reading of the insets here is a
  // second opinion waiting to drift from the one the width came from.
  const bar = resolveVerticalBar(state);
  if (bar.width === 0) return NO_HOUSING;
  const ends: HousingEnds = {
    head: state.barHeadOccluded ?? true,
    // The ONE place `reserved` is spent as a fallback: rule 2 against rule 3 above.
    foot: state.barFootOccluded ?? bar.reserved,
  };
  // 20260926 - the extents ride on a REPORTED end only; a fallback end never has one. Added as
  // keys only when known, so an unmeasured pose resolves to exactly the object it always did.
  const headExtent = knownExtent(state.barHeadOccluded, state.barHeadExtent);
  const footExtent = knownExtent(state.barFootOccluded, state.barFootExtent);
  if (headExtent !== undefined) ends.headExtent = headExtent;
  if (footExtent !== undefined) ends.footExtent = footExtent;
  return ends;
}

/**
 * The width alone, for the consumers that only ever needed the number: the `--safe-*` publisher
 * (foldStateStore), the harness's rail guide, and every existing call site. A thin read of
 * resolveVerticalBar() and NOT a second implementation — one decision, two shapes of answer.
 */
export function resolveVerticalBarWidth(
  state: Pick<FoldState, 'verticalBarEdge' | 'safeArea' | 'splitView'>,
): number {
  return resolveVerticalBar(state).width;
}

/**
 * The safe rect as the PAGE must treat it: the measured insets, with a synthesized rail folded into
 * the docked edge when we carved one.
 *
 * This is what foldStateStore publishes as `--safe-left/top/right/bottom`, and it is the half that
 * makes the synthesized rail real. The dock is `position: fixed` against the window edge, so on its
 * own it would simply be drawn ON TOP of the board — the content only moves out of its way because
 * the shells pad by `--safe-*` (and every centred dialog pads by `--scrim-inset`, cs:6133, which is
 * built from the same four). Publishing the measured zero there while the dock claims 84px would
 * put a column of controls over the content it is supposed to sit beside.
 *
 * Only ever WIDENS the docked side, and only when `resolveVerticalBarWidth` synthesized: a measured
 * inset is passed through untouched (the system's number stays the system's), and a refused narrow
 * strip keeps its real inset too — the system still reserved it, we just declined to dock in it.
 */
export function resolveSafeArea(
  state: Pick<FoldState, 'verticalBarEdge' | 'safeArea' | 'splitView'>,
): SafeAreaInsets {
  const edge = state.verticalBarEdge;
  if (edge === null) return state.safeArea;
  const width = resolveVerticalBarWidth(state);
  const inset = edge === 'trailing' ? state.safeArea.right : state.safeArea.left;
  if (width <= inset) return state.safeArea;
  return edge === 'trailing'
    ? { ...state.safeArea, right: width }
    : { ...state.safeArea, left: width };
}

/**
 * 20260929 - HOW FAR A ROW THAT HUGS THE WINDOW'S TOP EDGE MUST STAY OFF AN UNCOVERED DISPLAY
 * CORNER, in CSS px. A MEASURED STOP-GAP, not a device dimension, and it stays one only until a
 * native corner source exists.
 *
 * THE DEFECT. The closed landscape pose with the strip on the LEADING edge reports L84/T0/R0/B34,
 * and every one of those numbers is honoured — yet the Timeline tab's trailing header control sat
 * under the display's top-trailing ROUNDED corner (a 59-pt radius on the outer display), which no
 * rectangular inset covers because top = right = 0 there. Nothing that reaches the web layer
 * (UIKit's four insets, the setFoldState payload, this store, the CSS aliases) carries corner
 * geometry, so the web cannot see it; this constant is the reach of that corner at y = 1 (59 −
 * √(59² − 58²) ≈ 48.2). A 55-pt corner (the inner display) reaches 44.6 at the same height, so
 * one number covers both displays.
 *
 * THE PROPER SOURCE IS NATIVE: iOS 26's corner-adapted safe area
 * (`UIView.LayoutRegion.safeArea(cornerAdaptation:)`, to be confirmed against the SDK headers) or
 * the display's corner radii pushed as new setFoldState fields. When either lands, this constant
 * and resolveUncoveredTopCornerSide() below give way to it.
 */
export const DISPLAY_CORNER_TOP_CLEARANCE_PX = 48;

/**
 * 20260929 - WHICH TOP CORNER OF THE WINDOW IS A LARGE-RADIUS DISPLAY CORNER THAT NO INSET COVERS:
 * `'left'`, `'right'`, or `null` for "none we can name".
 *
 * Non-null only when all three hold:
 *   • a dock is in effect (resolveVerticalBar's width > 0) — no strip, no evidence about corners;
 *   • the strip is the SYSTEM's (`reserved`), not a rail we carved in a pane — a carved rail says
 *     nothing about where the display's corners are;
 *   • the system's furniture is at the strip's HEAD (resolveHousingEnds, which includes the
 *     no-reading fallback: "wasted space, never a control under a camera").
 * The answer is then the top corner OPPOSITE the dock: the housing end of the strip sits on the
 * long edge whose two ends carry the large-radius corners, and the far one is uncovered.
 *
 * NO ORIENTATION TEST HERE, deliberately: a rotation that leaves the four insets unchanged (closed
 * portrait ↔ the strip-trailing landscape) would not re-run the publisher, so a JS aspect test
 * would freeze one rotation behind. The landscape gate lives in global.css (`--corner-top-*`), and
 * consumers read only those. Closed portrait answers `'left'` here and the gate spends 0 — its
 * uncovered top corner is a small hinge-side one.
 *
 * NEVER FOLD THIS INTO `--safe-left/right`: deriveVerticalBarEdge needs the non-strip side < 22,
 * so a 48 there would un-dock the very pose this fixes, and every row of every tab would pad by it.
 */
export function resolveUncoveredTopCornerSide(
  state: Pick<FoldState,
    'verticalBarEdge' | 'safeArea' | 'splitView' | 'barHeadOccluded' | 'barFootOccluded'>,
): 'left' | 'right' | null {
  const bar = resolveVerticalBar(state);
  if (bar.width === 0 || !bar.reserved) return null;
  if (!resolveHousingEnds(state).head) return null;
  return state.verticalBarEdge === 'leading' ? 'right' : 'left';
}


// ── Reading env(safe-area-inset-*) from script ──────────────────────────────────────────────
//
// There is no API for this. `env()` is a CSS value function, readable only where CSS values are
// read, so the only way to get the numbers into TypeScript is to put them on a real element's
// computed style and read them back. Those elements are created ONCE and kept: creating them per
// read would add nodes + a style recalc to every resize event, and a fold/unfold is a resize.
//
// `padding` rather than `inset`/`margin` because padding cannot go negative and is not subject to
// collapsing, so a bad `env()` fallback can only ever read as 0.

const ZERO_INSETS: SafeAreaInsets = { left: 0, top: 0, right: 0, bottom: 0 };

/** One of the four sides of the safe rect, so the probe table below can be written once and read
 *  once rather than spelled out twice and allowed to drift. */
type Side = keyof SafeAreaInsets;

const PADDING_PROPERTY = {
  top: 'paddingTop',
  right: 'paddingRight',
  bottom: 'paddingBottom',
  left: 'paddingLeft',
} as const satisfies Record<Side, keyof CSSStyleDeclaration>;

/**
 * 20260920 - TWO PROBES, AND THE PARTITION BETWEEN THEM IS THE WHOLE POINT.
 *
 * THE DEFECT. This was ONE 0×0 element carrying all four insets as `padding: top right bottom
 * left`, so its border box was `(left + right) × (top + bottom)`. foldStateStore's watcher observes
 * that border box because it is the only signal an inset-only change produces on this platform
 * (that file carries the reasoning). A 180° turn of the closed landscape pose SWAPS left and right
 * — 84/0 becomes 0/84 — and a sum does not notice a swap: the box was identical to the pixel, the
 * observer never fired, and nothing else re-read either, because the window is the same size in the
 * same orientation class so `resize` does not fire. The owner photographed the result on 20260920:
 * the page inset itself correctly on the new side (global.css resolves `--safe-*` straight from
 * `env()`, so the CSS half self-heals) while the dock stayed drawn on the old side, over the panel,
 * with its camera clearance still at the wrong end. Exactly the fingerprint cs:6132 described — the
 * JS-resolved half frozen one rotation behind the CSS half — one rung short of being fixed by it.
 *
 * THE RULE THAT REPLACES IT: every inset must be the SOLE determinant of one dimension of one
 * observed box. So each probe takes one vertical side and one horizontal side, and the four land on
 * four different dimensions:
 *
 *     'top-left'      padding-top + padding-left      → border box  left  × top
 *     'right-bottom'  padding-right + padding-bottom  → border box  right × bottom
 *
 * A change to ANY one of the four now changes a box, whatever the other three do. Collapse these
 * back into one element and the swap goes invisible again — foldState.test.ts pins the arithmetic
 * of both shapes side by side so the regression states itself rather than reappearing on a device.
 *
 * TWO IS ENOUGH AND FOUR WOULD NOT BE BETTER: a box has two dimensions, so two boxes hold four
 * independent numbers, and each extra node is another element in the document and another
 * getComputedStyle per rung of the settle ladder.
 *
 * ONE TABLE, BOTH USES. The css and the read below are both generated from this, so a probe cannot
 * end up carrying a side that nothing reads off it, or being read for a side it does not carry —
 * which would silently read 0 forever.
 */
const PROBES = [
  { key: 'top-left', vertical: 'top', horizontal: 'left' },
  { key: 'right-bottom', vertical: 'bottom', horizontal: 'right' },
] as const satisfies readonly { key: string; vertical: Side; horizontal: Side }[];

/** Empty until the first read, and replaced WHOLE — never half-rebuilt, so the pair always
 *  describes one document. */
let probes: HTMLElement[] = [];

/** parseFloat with the two failure modes folded into 0: jsdom returns '' for a declaration it could
 *  not parse (it does not understand `env()`), and a host with no safe area returns '0px'. */
function px(value: string): number {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * The four insets the browser currently reports, in CSS px.
 *
 * Zeros — never a throw — when there is no document, no getComputedStyle, or no body yet (jsdom,
 * SSR, a call from `<head>`). This is read during store creation, which is module-evaluation time
 * for anything that imports the store, so a throw here would take down boot and every suite that
 * touches a component with a side dock in it.
 *
 * Zeros are also the correct answer in the embedded WebView until the cover viewport is on — the
 * pre-paint block in index.html turns it on before first paint, so by the time anything renders the
 * numbers are real. (Do not spell that meta token out anywhere in src/**.ts(x): theme/__tests__/
 * viewportCover.test.ts counts every file containing it as a route that turns cover on at runtime,
 * and a mention in prose breaks the count.)
 */
export function readEnvSafeArea(): SafeAreaInsets {
  try {
    if (typeof document === 'undefined') return { ...ZERO_INSETS };
    if (typeof getComputedStyle !== 'function') return { ...ZERO_INSETS };

    // `isConnected` re-creates the probes if something wiped document.body between reads —
    // testing-library's cleanup does exactly that, and a detached node computes to nothing. ALL OR
    // NOTHING (20260920): half a pair would leave one side of the safe rect frozen at whatever the
    // surviving node last computed, which is the same class of silent staleness the split fixed.
    if (probes.length !== PROBES.length || probes.some((p) => p.isConnected === false)) {
      if (document.body === null) return { ...ZERO_INSETS };
      for (const stale of probes) stale.remove();
      probes = PROBES.map(({ key, vertical, horizontal }) => {
        const el = document.createElement('div');
        el.setAttribute('aria-hidden', 'true');
        // The VALUE names the two sides this node carries, so the DOM says the partition out loud.
        el.setAttribute('data-safe-area-probe', key);
        // Zero-sized, fixed, hidden and non-interactive: it must not affect layout, hit-testing,
        // scroll extent or the accessibility tree. `visibility:hidden` (not `display:none`) is
        // required — a display:none element has no computed padding to read.
        el.style.cssText =
          'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;'
          + `padding-${vertical}:env(safe-area-inset-${vertical},0px);`
          + `padding-${horizontal}:env(safe-area-inset-${horizontal},0px);`;
        document.body.appendChild(el);
        return el;
      });
    }

    const insets = { ...ZERO_INSETS };
    PROBES.forEach(({ vertical, horizontal }, index) => {
      const cs = getComputedStyle(probes[index]);
      insets[vertical] = px(cs[PADDING_PROPERTY[vertical]]);
      insets[horizontal] = px(cs[PADDING_PROPERTY[horizontal]]);
    });
    return insets;
  } catch {
    // Reading a safe area must never be able to block boot or break a render.
    return { ...ZERO_INSETS };
  }
}

/**
 * 20260919 - IPHONE DUO SIDE DOCK: the probe NODES, for the one caller that must OBSERVE them
 * rather than read them.
 *
 * foldStateStore's watcher hangs a ResizeObserver off these elements because a reserved side strip
 * can appear with NO window resize at all (that file carries the full reasoning). The nodes are not
 * stable for the life of the document — readEnvSafeArea re-creates them whenever something has
 * wiped document.body under them, which testing-library's cleanup does on every test — so an
 * observer bound once would end up silently watching detached nodes forever, holding references to
 * them and delivering nothing. Handing back the CURRENT nodes lets the watcher re-check identity on
 * each re-read and re-attach when they changed.
 *
 * 20260920 - PLURAL, AND EVERY ONE OF THEM HAS TO BE OBSERVED. See PROBES above: a single box whose
 * width is `left + right` cannot see the two sides swap, which is the defect this split closes. A
 * watcher that observed only the first node would have half the blindness back.
 *
 * EMPTY before the first read, and in any host with no document. That is a real state, not an edge
 * case: the store seeds itself at module-evaluation time, which can run before document.body exists.
 */
export function getEnvSafeAreaProbes(): readonly HTMLElement[] {
  return probes;
}

/** Test seam only: drops the cached probes so the next read builds fresh ones. Never called by the
 *  app — they live as long as the document does. BOTH, always: see the all-or-nothing note in
 *  readEnvSafeArea. */
export function resetEnvSafeAreaProbe(): void {
  for (const el of probes) el.remove();
  probes = [];
}
